export function isGrowthMetric(metric) {
  return Boolean(metric?.key && String(metric.key).toLowerCase().includes("growth"))
}

export function readPlatformRaw(row, platform, metricKey) {
  const raw = row?.platformMetrics?.[platform]?.[metricKey]?.raw
  if (raw == null) return 0
  const numeric = Number(raw)
  return Number.isFinite(numeric) ? numeric : 0
}

function niceAxisStep(range, maxTicks = 5) {
  const span = Math.max(Math.abs(range), 1)
  const raw = span / Math.max(1, maxTicks - 1)
  const exponent = Math.floor(Math.log10(raw))
  const fraction = raw / 10 ** exponent
  let niceFraction
  if (fraction <= 1) niceFraction = 1
  else if (fraction <= 2) niceFraction = 2
  else if (fraction <= 5) niceFraction = 5
  else niceFraction = 10
  return niceFraction * 10 ** exponent
}

export function buildGrowthAxis(values, maxTicks = 5) {
  const nums = values.filter((value) => Number.isFinite(value))
  const dataMax = nums.length ? Math.max(...nums) : 0
  const dataMin = nums.length ? Math.min(...nums) : 0
  let yMin = 0
  let yMax = 50

  if (dataMin >= 0) {
    const step = niceAxisStep(Math.max(dataMax, 1), maxTicks)
    yMax = Math.max(step, Math.ceil(dataMax / step) * step)
    yMin = 0
  } else if (dataMax <= 0) {
    const step = niceAxisStep(Math.max(Math.abs(dataMin), 1), maxTicks)
    yMin = Math.min(-step, Math.floor(dataMin / step) * step)
    yMax = 0
  } else {
    const abs = Math.max(Math.abs(dataMin), Math.abs(dataMax))
    const step = niceAxisStep(abs * 2, maxTicks)
    const bound = Math.max(step, Math.ceil(abs / step) * step)
    yMax = bound
    yMin = -bound
  }

  const range = yMax - yMin || 1
  const step = niceAxisStep(range, maxTicks)
  const alignedMax = Math.ceil(yMax / step) * step
  const alignedMin = Math.floor(yMin / step) * step
  const ticks = []
  for (let value = alignedMax; value >= alignedMin - step * 0.001; value -= step) {
    ticks.push(Number(value.toFixed(4)))
  }
  if (alignedMin < 0 && alignedMax > 0 && !ticks.some((tick) => Math.abs(tick) < step * 0.001)) {
    ticks.push(0)
    ticks.sort((a, b) => b - a)
  }

  return { yMin: alignedMin, yMax: alignedMax, ticks }
}

export function barPlacement(value, yMin, yMax) {
  const range = yMax - yMin || 1
  const height = (Math.abs(value) / range) * 100
  if (value >= 0) {
    return { bottom: `${((0 - yMin) / range) * 100}%`, height: `${height}%` }
  }
  return { top: `${((yMax - 0) / range) * 100}%`, height: `${height}%` }
}

export function formatGrowthTick(value) {
  const numeric = Number(value)
  if (!Number.isFinite(numeric)) return ""
  const rounded = Math.round(numeric)
  if (rounded === 0) return "0%"
  const abs = Math.abs(rounded)
  const prefix = rounded > 0 ? "+" : "-"
  if (abs >= 1000) {
    const compact =
      abs % 1000 === 0 ? `${abs / 1000}k` : `${(abs / 1000).toFixed(1).replace(/\.0$/, "")}k`
    return `${prefix}${compact}%`
  }
  return `${prefix}${abs}%`
}

export function growthToneClass(raw) {
  const numeric = Number(raw)
  if (!Number.isFinite(numeric)) return ""
  const rounded = Math.round(numeric)
  if (rounded === 0) return ""
  return rounded > 0 ? "is-up" : "is-down"
}
